import type { JSX } from 'react'
import {
  ArrowUp,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  CircleX,
  Copy,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  Funnel,
  GripVertical,
  Pin,
  Info,
  House,
  AppWindow,
  ShieldCheck,
  LogOut,
  Link,
  LoaderCircle,
  Menu,
  MessageSquare,
  Monitor,
  Moon,
  PanelRightClose,
  Plus,
  RotateCcw,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Table2,
  TriangleAlert,
  X,
  type LucideProps
} from 'lucide-react'

/**
 * The glyphs this console renders.
 *
 * Registered explicitly rather than re-exporting all of lucide: a namespace
 * import defeats tree-shaking and pulls the whole icon set into the bundle.
 * Add a row when a screen needs a glyph.
 */
const REGISTRY = {
  home: House,
  'app-window': AppWindow,
  'shield-check': ShieldCheck,
  info: Info,
  'triangle-alert': TriangleAlert,
  'circle-x': CircleX,
  'log-out': LogOut,
  menu: Menu,
  x: X,
  // The date-range trigger, the table's sort marks and the pager's four steps.
  'calendar-days': CalendarDays,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevrons-up-down': ChevronsUpDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  // The themes. On the sign-in toggle each glyph names the theme a press
  // SWITCHES TO, not the one on screen, which is why it shows a moon in light.
  // In the account menu each glyph labels its own choice, and the monitor
  // is System: follow the OS.
  sun: Sun,
  moon: Moon,
  monitor: Monitor,
  // The merchant picker: the search box, and the mark on a chosen row.
  search: Search,
  check: Check,
  // The column picker. The trigger names what it configures; the grip is the
  // drag handle on a row; and the arrow undoes the reader's whole layout back
  // to the screen's own.
  settings: Settings,
  'grip-vertical': GripVertical,
  pin: Pin,
  'rotate-ccw': RotateCcw,
  // The per-column filter control in a table header. One glyph for both
  // states: a filled funnel would read as a second, different control, so the
  // active column says so with a mark beside the glyph and in the trigger's
  // own name.
  funnel: Funnel,
  // A table's copy-a-shareable-layout control.
  link: Link,
  copy: Copy,
  download: Download,
  // The rail's customize mode: the control that opens it, and each page's
  // shown or hidden state inside it.
  'sliders-horizontal': SlidersHorizontal,
  eye: Eye,
  'eye-off': EyeOff,
  // The report chat: send, a running step, the agent's mark, a draft, a new
  // chat, a chat in the list, closing the draft panel, and a draft's own page.
  'arrow-up': ArrowUp,
  loader: LoaderCircle,
  sparkles: Sparkles,
  table: Table2,
  plus: Plus,
  'message-square': MessageSquare,
  'panel-right-close': PanelRightClose,
  'external-link': ExternalLink
} as const

export type IconName = keyof typeof REGISTRY

export interface IconProps extends Omit<LucideProps, 'ref'> {
  name: IconName
}

/**
 * Icon — a lucide glyph.
 *
 * Hidden from assistive technology: a glyph never carries the meaning on its
 * own, so its label lives on the control that contains it.
 *
 * The only wrapper here that does **not** require `data-testid`, deliberately:
 * an `aria-hidden` decoration is never a test target, and the control around
 * it already carries the hook.
 *
 * @param props - Icon props.
 * @param props.name - Registered glyph name.
 * @returns The rendered glyph.
 */
export function Icon({ name, ...props }: IconProps): JSX.Element {
  const Glyph = REGISTRY[name]

  return <Glyph aria-hidden="true" {...props} />
}
